// src/pages/TermsAndConditionsPage.jsx
import React, { useEffect, useState } from "react";
import NavBar from "../components/NavBar";

const MatrixTermsBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("matrix-terms-bg");
    const context = canvas.getContext("2d");
    let width = window.innerWidth,
      height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    const katakana = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    const latin = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const alphabet = katakana + latin + nums;
    const fontSize = 16;
    const columns = width / fontSize;
    const rainDrops = new Array(Math.floor(columns)).fill(1);
    const draw = () => {
      context.fillStyle = "rgba(0, 0, 0, 0.05)";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#0F0";
      context.font = fontSize + "px monospace";
      for (let i = 0; i < rainDrops.length; i++) {
        const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        context.fillText(text, i * fontSize, rainDrops[i] * fontSize);
        if (rainDrops[i] * fontSize > height && Math.random() > 0.975) {
          rainDrops[i] = 0;
        }
        rainDrops[i]++;
      }
    };
    const interval = setInterval(draw, 30);
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <canvas
      id="matrix-terms-bg"
      className="fixed top-0 left-0 w-full h-full opacity-20 pointer-events-none"
    />
  );
};

const TermsAndConditionsPage = () => {
  const [visible, setVisible] = useState(false);
  // Optionally, you can compute the effective date here
  const effectiveDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <section className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
      <NavBar />
      <MatrixTermsBackground />
      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Card Container */}
        <div
          className={`max-w-4xl mx-auto bg-gray-800 bg-opacity-95 p-8 rounded-lg shadow-2xl transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <header className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold text-green-400 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-lg text-gray-300">
              These Terms and Conditions govern your use of the AIOrbis website and services. Please read them carefully.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Effective Date: {effectiveDate}
            </p>
          </header>
          <article className="space-y-6 text-base leading-7 text-gray-200">
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">1. Introduction</h2>
              <p>
                Welcome to AIOrbis. These Terms and Conditions ("Terms") govern your access to and use of our website, products, and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy, which is incorporated herein by reference.
              </p>
              <p>
                AIOrbis is committed to providing innovative digital solutions and ensuring a safe online environment. We reserve the right to modify these Terms at any time. Your continued use of our Services constitutes your acceptance of any changes.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">2. Definitions</h2>
              <p>
                In these Terms:
              </p>
              <ul className="list-disc ml-6">
                <li><strong>"User"</strong> means any individual or entity that accesses or uses our Services.</li>
                <li><strong>"Content"</strong> includes all text, images, videos, and other material posted or transmitted on our website.</li>
                <li><strong>"Account"</strong> refers to any registered account created by a User to access certain features of our Services.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">3. Acceptance of Terms</h2>
              <p>
                By using our Services, you confirm that you have read, understood, and agree to these Terms. If you do not agree with any part of these Terms, you must immediately cease using our Services.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">4. Use of Services</h2>
              <p>
                You agree to use our Services only for lawful purposes and in accordance with these Terms. You shall not engage in any activities that could harm, disable, or impair the Services or interfere with other users' enjoyment of the Services.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">5. User Obligations and Account Security</h2>
              <p>
                If you create an account, you are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You agree to notify AIOrbis immediately if you suspect any unauthorized access or use.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">6. Intellectual Property Rights</h2>
              <p>
                All content, trademarks, logos, and intellectual property displayed on the AIOrbis website are owned by AIOrbis or its licensors. You are not permitted to use, reproduce, distribute, or modify any content without our explicit written consent.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">7. Payment Terms</h2>
              <p>
                When purchasing services through our website, you agree to pay all applicable fees as set forth at the time of purchase. All payments are non-refundable except as expressly stated in our refund policy.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">8. Prohibited Conduct</h2>
              <p>
                You agree not to:
              </p>
              <ul className="list-disc ml-6">
                <li>Use our Services for any illegal purpose;</li>
                <li>Interfere with the security or operation of our Services;</li>
                <li>Introduce malicious software or viruses;</li>
                <li>Impersonate any person or entity;</li>
                <li>Attempt to gain unauthorized access to our systems.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">9. Disclaimers</h2>
              <p>
                Our Services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. AIOrbis does not guarantee that our Services will be uninterrupted or error‑free.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">10. Limitation of Liability</h2>
              <p>
                In no event shall AIOrbis be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of your use of our Services—even if advised of the possibility of such damages. This limitation applies to all claims, whether based on warranty, contract, tort, or any other legal theory.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">11. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless AIOrbis and its affiliates from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys’ fees) arising from your use of our Services or your breach of these Terms.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">12. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction] without regard to conflict of law principles. Any disputes arising under or in connection with these Terms shall be resolved exclusively through binding arbitration in [Location] in accordance with the rules of [Arbitration Organization].
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">13. Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">14. Termination</h2>
              <p>
                AIOrbis reserves the right to suspend or terminate your access to our Services immediately, without notice, if you breach these Terms or engage in conduct that is harmful to the interests of AIOrbis or its users.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">15. Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and AIOrbis regarding your use of our Services and supersede any prior agreements or understandings.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">16. Amendments</h2>
              <p>
                AIOrbis reserves the right to modify these Terms at any time. Any amendments will be effective immediately upon posting on our website, and your continued use of our Services constitutes acceptance of the updated Terms.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">17. Miscellaneous</h2>
              <p>
                The failure of AIOrbis to enforce any provision of these Terms shall not be deemed a waiver of that provision. All notices or communications provided by AIOrbis will be sent via email or published on our website.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">18. Contact Information</h2>
              <p>
                For any questions regarding these Terms, please contact us at:
              </p>
              <p>
                <strong>AIOrbis</strong><br />
                Email: legal@aiorbis.com<br />
                Address: 1234 Innovation Drive, Tech City, Country
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">19. Effective Date</h2>
              <p>
                These Terms and Conditions are effective as of {effectiveDate}. By using our Services, you acknowledge and agree to these Terms as of this effective date.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
};

export default TermsAndConditionsPage;
